import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Download, 
  FileText, 
  Phone,
  ExternalLink,
  Info,
  Calendar,
  Users,
  Heart,
  Stethoscope,
  Shield
} from 'lucide-react';
import { hospitalInfo } from '../../mock';
import { useBusinessInfo } from '../../hooks/useBusinessInfo';

const FormsDownload = () => {
  const { businessInfo: currentBusinessInfo } = useBusinessInfo();
  const primaryColor = 'var(--theme-primary)';
  const primaryBg = 'var(--theme-primary-light)';
  const [formCategories, setFormCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  React.useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/forms/visible`);
      if (response.ok) {
        const forms = await response.json();
        
        // Group forms by category
        const grouped = forms.reduce((acc, form) => {
          const existingCategory = acc.find(cat => cat.title === form.category);
          
          const formData = {
            name: form.name,
            description: form.description,
            onlineLink: form.online_link,
            downloadLink: form.download_link
          };
          
          if (existingCategory) {
            existingCategory.forms.push(formData);
          } else {
            acc.push({
              title: form.category,
              description: form.description,
              icon: getIconComponent(form.category_icon),
              color: form.category_color,
              forms: [formData]
            });
          }
          
          return acc;
        }, []);
        
        setFormCategories(grouped);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIconComponent = (iconName) => {
    const iconMap = {
      'Users': Users,
      'Calendar': Calendar,
      'Shield': Shield,
      'FileText': FileText,
      'Stethoscope': Stethoscope
    };
    return iconMap[iconName] || FileText;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading forms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Forms Categories */}
      <section style={{ paddingTop: '32px', paddingBottom: '32px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {formCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
                {/* Category Header - More Compact */}
                <div className="px-6 py-4 border-b border-gray-100" style={{ backgroundColor: `${category.color}08` }}>
                  <div className="flex items-center">
                    <div 
                      className="flex items-center justify-center w-10 h-10 rounded-xl mr-3 shadow-sm" 
                      style={{ backgroundColor: `${category.color}20` }}
                    >
                      <category.icon className="h-5 w-5" style={{ color: category.color }} />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-gray-900">{category.title}</h2>
                      <p className="text-xs text-gray-600 mt-0.5">{category.description}</p>
                    </div>
                  </div>
                </div>
                
                {/* Forms Grid */}
                <div className="p-6">

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {category.forms.map((form, formIndex) => (
                      <div 
                        key={formIndex} 
                        className="group relative bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-5 hover:border-gray-300 hover:shadow-lg transition-all duration-300"
                      >
                        <div className="absolute top-0 left-0 w-1 h-full rounded-l-xl transition-all duration-300 group-hover:w-2" style={{ backgroundColor: category.color }}></div>
                        <div className="pl-2">
                          <h3 className="text-sm font-bold text-gray-900 mb-1.5">{form.name}</h3>
                          <p className="text-xs text-gray-600 mb-4 leading-relaxed">{form.description}</p>
                          
                          <div className="flex flex-col sm:flex-row gap-2.5">
                            {form.onlineLink && (
                              <Link
                                to={form.onlineLink}
                                className="inline-flex items-center justify-center px-3.5 py-2 rounded-lg text-white text-xs font-semibold transition-all duration-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                                style={{ backgroundColor: category.color }}
                                onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                                onMouseLeave={(e) => e.target.style.opacity = '1'}
                              >
                                <FileText className="mr-1.5 h-3.5 w-3.5" />
                                Fill Online
                              </Link>
                            )}
                            
                            <a
                              href={form.downloadLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center px-3.5 py-2 rounded-lg border-2 text-xs font-semibold transition-all duration-300 hover:shadow-md transform hover:-translate-y-0.5"
                              style={{ borderColor: category.color, color: category.color }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = category.color;
                                e.target.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = category.color;
                              }}
                            >
                              <Download className="mr-1.5 h-3.5 w-3.5" />
                              Download
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-white" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Need Help with Forms?
            </h2>
            <p className="text-gray-600">
              Our team is here to assist you with any questions about these forms or your upcoming visit.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-6 rounded-xl text-center">
              <Phone className="h-8 w-8 mx-auto mb-4" style={{ color: primaryColor }} />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Call Us</h3>
              <p className="text-gray-700 mb-4">Speak with our team directly</p>
              <a
                href={`tel:${currentBusinessInfo.phone}`}
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-white font-semibold transition-colors duration-200"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
              >
                <Phone className="mr-2 h-5 w-5" />
                {currentBusinessInfo.phone}
              </a>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl text-center">
              <Heart className="h-8 w-8 mx-auto mb-4" style={{ color: primaryColor }} />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Schedule Visit</h3>
              <p className="text-gray-700 mb-4">Book your appointment online</p>
              <Link
                to="/reach-us"
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg border-2 font-semibold transition-colors duration-200"
                style={{ borderColor: primaryColor, color: primaryColor }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = primaryColor;
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = primaryColor;
                }}
              >
                <Calendar className="mr-2 h-5 w-5" />
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FormsDownload;